"use client";

function normalizeCompletionScope(value: string): string {
  const normalized = value.trim();
  return normalized || "unknown";
}

type CompletionScreenPayload = {
  systemId: string;
  connectorName: string;
};

export function getCompletionScreenSessionKey(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): string {
  const sessionScope = workspaceId.trim() || "none";
  const completionScope =
    systemId.trim() || normalizeCompletionScope(connectorName);

  return `connector-onboarding-completed:${sessionScope}:${completionScope}`;
}

export function getCompletionScreenPendingKey(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): string {
  return `${getCompletionScreenSessionKey(workspaceId, systemId, connectorName)}:pending`;
}

export function getCompletionScreenActiveKey(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): string {
  return `${getCompletionScreenSessionKey(workspaceId, systemId, connectorName)}:active`;
}

export function getCompletionScreenPayloadKey(workspaceId: string): string {
  const sessionScope = workspaceId.trim() || "none";
  return `connector-onboarding-completed:${sessionScope}:payload`;
}

export function hasSeenCompletionScreen(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(
    sessionStorage.getItem(
      getCompletionScreenSessionKey(workspaceId, systemId, connectorName),
    ),
  );
}

export function markCompletionScreenSeen(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.setItem(
    getCompletionScreenSessionKey(workspaceId, systemId, connectorName),
    "1",
  );
}

export function clearCompletionScreenSeen(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.removeItem(
    getCompletionScreenSessionKey(workspaceId, systemId, connectorName),
  );
}

export function armCompletionScreen(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.setItem(
    getCompletionScreenPendingKey(workspaceId, systemId, connectorName),
    "1",
  );
  sessionStorage.setItem(
    getCompletionScreenPayloadKey(workspaceId),
    JSON.stringify({
      systemId,
      connectorName,
    } satisfies CompletionScreenPayload),
  );
}

export function consumeArmedCompletionScreen(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const key = getCompletionScreenPendingKey(workspaceId, systemId, connectorName);
  const isArmed = Boolean(sessionStorage.getItem(key));
  if (!isArmed) {
    return false;
  }

  sessionStorage.removeItem(key);
  sessionStorage.removeItem(getCompletionScreenPayloadKey(workspaceId));
  return true;
}

export function getArmedCompletionScreenPayload(
  workspaceId: string,
): CompletionScreenPayload | null {
  if (typeof window === "undefined") {
    return null;
  }

  const rawPayload = sessionStorage.getItem(getCompletionScreenPayloadKey(workspaceId));
  if (!rawPayload) {
    return null;
  }

  try {
    const parsedPayload = JSON.parse(rawPayload) as Partial<CompletionScreenPayload>;
    const systemId = parsedPayload.systemId?.trim() ?? "";
    const connectorName = parsedPayload.connectorName?.trim() ?? "";

    if (!systemId && !connectorName) {
      return null;
    }

    return { systemId, connectorName };
  } catch {
    return null;
  }
}

export function hasActiveCompletionScreen(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(
    sessionStorage.getItem(
      getCompletionScreenActiveKey(workspaceId, systemId, connectorName),
    ),
  );
}

export function markCompletionScreenActive(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.setItem(
    getCompletionScreenActiveKey(workspaceId, systemId, connectorName),
    "1",
  );
}

export function clearCompletionScreenDisplayState(
  workspaceId: string,
  systemId: string,
  connectorName: string,
): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.removeItem(
    getCompletionScreenActiveKey(workspaceId, systemId, connectorName),
  );
  sessionStorage.removeItem(
    getCompletionScreenPendingKey(workspaceId, systemId, connectorName),
  );
  sessionStorage.removeItem(getCompletionScreenPayloadKey(workspaceId));
}
